import { format, startOfDay } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

// How dates are shown to the user. Rendering goes through the UI date components
// (DateDisplay, DateSelect, DateTimeSelect, TimeSelect); where a string is needed instead of
// an element — table accessors, chart labels, spreadsheet cells, option labels — use
// formatDate / formatDateLocale from here, so the rules below still apply in one place.
//
// Which timezone stored dates are shown in. The server pins business dates (order start/end,
// postings, lot and container dates) to midnight at the operating location — see
// sopkoa/helpers/businessDayHelper — so 1 Oct in Edmonton is stored as 2026-10-01T06:00Z.
// Formatting that in the browser's own timezone shows 30 Sep to anyone whose machine
// resolves to Pacific, and they then pick dates that look consistent on their screen but
// aren't in the data. So stored values are shown in the user's profile timezone (the
// login token's `timezone`: membership, then default location, then the tenant's), and
// everyone sees the same day whatever their machine thinks.

let profileTimezone = null;

export function setProfileTimezone(timezone) {
    profileTimezone = validTimezone(timezone);
}

export function businessTimezone() {
    if (profileTimezone != null) return profileTimezone;
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return null;
    }
}

function validTimezone(value) {
    if (typeof value !== 'string' || value === '') return null;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return value;
    } catch {
        return null;
    }
}

function toDate(value) {
    if (value == null || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
}

// Exactly midnight UTC is how a date-only value reaches the database (uploads, migrations,
// integration APIs); businessDayStart passes it through untouched. It names its UTC day,
// which any timezone behind UTC would drag back a day.
function isUtcMidnight(date) {
    return date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0 && date.getUTCMilliseconds() === 0;
}

// True when a date-fns format string renders a time of day (quoted literals ignored).
function hasTimeTokens(formatString) {
    return /[HhKkmsSaBb]/.test(String(formatString).replace(/'[^']*'/g, ''));
}

// A Date whose *local* fields read as the instant does in the profile timezone.
function wallClock(date, dateOnly) {
    if (dateOnly && isUtcMidnight(date))
        return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    const timezone = businessTimezone();
    return timezone != null ? toZonedTime(date, timezone) : date;
}

/** Formats a stored value as it reads in the profile timezone, with a date-fns format string. */
export function formatDate(value, formatString = 'yyyy-MM-dd') {
    const date = toDate(value);
    return date != null ? format(wallClock(date, !hasTimeTokens(formatString)), formatString) : null;
}

/**
 * The toLocaleDateString / toLocaleString / toLocaleTimeString equivalent, in the profile
 * timezone. `options` are Intl.DateTimeFormat options; with none, `style` picks the default
 * the matching toLocale* method would use.
 */
export function formatDateLocale(value, options, locale, style = 'date') {
    const date = toDate(value);
    if (date == null) return null;
    const opts = options != null && Object.keys(options).length > 0 ? options :
        style === 'time' ? { hour: 'numeric', minute: '2-digit', second: '2-digit' } :
        style === 'dateTime' ? { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' } :
        { year: 'numeric', month: 'numeric', day: 'numeric' };
    const hasTime = opts.hour != null || opts.minute != null || opts.second != null || opts.timeStyle != null;
    const timeZone = !hasTime && isUtcMidnight(date) ? 'UTC' : businessTimezone() ?? undefined;
    return new Intl.DateTimeFormat(locale, { ...opts, timeZone }).format(date);
}

/**
 * The value a date picker should open on. Stored values (ISO strings, timestamps) become the
 * day they name in the profile timezone. A Date is what the picker itself emitted — its local
 * clock reads the day the user chose, callers treat it by its local fields
 * (ConversionHelper.minDate, the PO receipt check) and the server reads it in the sender's
 * timezone (X-Timezone header) — so it comes back unchanged.
 */
export function toPickerValue(value) {
    if (value instanceof Date) return value;
    const date = toDate(value);
    return date != null ? wallClock(date, true) : null;
}

/**
 * A date-only picker value: local midnight of the day `value` names (stored values by their
 * date-only reading, a Date by its local clock). Sent as-is it reads as that day on the
 * server (sender-timezone midnight), and changing the day carries no hidden time with it.
 * Null/empty stays null.
 */
export function pickerDay(value) {
    const date = toPickerValue(value);
    return date != null && !isNaN(date.getTime()) ? startOfDay(date) : null;
}

/**
 * The day a schedule date falls on, as a wall-clock local midnight comparable with pickerToday().
 * All-day schedules store midnight UTC of their day and are read by that day; timed schedules are
 * real moments, read in the profile timezone.
 */
export function scheduleDay(value, allDay) {
    const date = toDate(value);
    if (date == null) return null;
    return startOfDay(allDay ? wallClock(date, true) : wallClock(date, false));
}

/** Today, as a date-only picker value (profile-timezone day, local midnight). */
export function pickerToday() {
    return startOfDay(businessNow());
}

// Calendar logic in the profile timezone — week strips, "today", due-date buckets. Build days
// as wall-clock Dates (local fields = the profile-timezone reading), do date-fns calendar maths
// on them, format them with formatWallClock, and turn them back into real instants with
// fromWallClock before they go into a query or get compared with stored timestamps.

/** Now, as a wall-clock Date in the profile timezone. */
export function businessNow() {
    return wallClock(new Date(), false);
}

/** The wall-clock Date a stored value reads as in the profile timezone (date-only values by their UTC day). */
export function businessDay(value) {
    const date = toDate(value);
    return date != null ? wallClock(date, true) : null;
}

/** The real instant a wall-clock Date (from businessNow / businessDay) stands for. */
export function fromWallClock(date) {
    const timezone = businessTimezone();
    return timezone != null ? fromZonedTime(date, timezone) : date;
}

/** Formats a wall-clock Date as-is — it is already in the profile timezone. */
export function formatWallClock(date, formatString) {
    return date != null ? format(date, formatString) : null;
}

/** A stored moment as a wall-clock Date in the profile timezone (no date-only reading). */
export function toWallClock(value) {
    const date = toDate(value);
    return date != null ? wallClock(date, false) : null;
}
